import { fetchAllEnvelopes, fetchUserRoles } from 'actions';
import { fetchCustomerContracts } from 'actions/customerContracts';
import { fetchAllDashboards } from 'actions/dashboard';
import { fetchAllIdentifiers } from 'actions/identifier';
import { fetchAllRuleGroups } from 'actions/ruleGroup';
import CreateCustomer from 'components/CreateCustomer';
import Customer from 'components/Customer';
import CustomerAdvanced from 'components/Customer/CustomerAdvanced';
import CustomerAllowedDomains from 'components/Customer/CustomerAllowedDomains';
import CustomerEditChrome from 'components/Customer/CustomerEditChrome';
import CustomerEditGlia from 'components/Customer/CustomerEditGlia';
import CustomerEditGmail from 'components/Customer/CustomerEditGmail';
import CustomerEditOffice from 'components/Customer/CustomerEditOffice';
import CustomerEditSlack from 'components/Customer/CustomerEditSlack';
import CustomerEditZendesk from 'components/Customer/CustomerEditZendesk';
import CustomerIndex from 'components/Customer/CustomerIndex';
import CustomerIndexHealth from 'components/Customer/CustomerIndexHealth';
import CustomerIndexingStatus from 'components/Customer/CustomerIndexingStatus';
import CustomerLogMetrics from 'components/Customer/CustomerLogMetrics';
import CustomerProcessingStatus from 'components/Customer/CustomerProcessingStatus';
import CustomerUploads from 'components/Customer/CustomerUploads';
import Dashboards from 'components/Customer/Dashboards';
import ExploreConfiguration from 'components/Customer/ExploreConfiguration';
import CustomerContractsManager from 'components/CustomerContractsManager';
import CustomerImport from 'components/CustomerImport';
import { RouteSpec, SuperRouteSpec } from 'types';
import { PARAMS as envelopeListQueryParams } from './envelopes';

const customerRoutes: (RouteSpec | SuperRouteSpec)[] = [
  {
    name: 'customer-allowed-domains',
    path: '/:customerDomain/customer/allowed-domains',
    component: CustomerAllowedDomains,
    private: true,
    permissions: 'customers.systemOptions',
  },
  {
    name: 'customer-dashboards',
    path: '/:customerDomain/customer/dashboards',
    component: Dashboards,
    private: true,
    permissions: 'customers.advancedOptions',
    data: {
      actions: [fetchAllDashboards, fetchUserRoles],
    },
  },
  {
    name: 'customer-explore-configuration',
    path: '/:customerDomain/customer/explore-configuration',
    component: ExploreConfiguration,
    private: true,
    permissions: 'customers.advancedOptions',
    data: {
      actions: [fetchAllIdentifiers, fetchUserRoles, fetchAllRuleGroups],
      params: {
        relationships: {
          defaultValue: [],
          list: true,
        },
        include_count: {
          defaultValue: '1',
        },
        identifiers: {
          nestedResource: true,

          params: {
            relationships: {
              defaultValue: [],
              list: true,
            },
            limit: {
              defaultValue: '-1',
            },
            order_by: {
              defaultValue: 'name',
            },
            selectable_fields: {
              list: true,
              defaultValue: ['name', 'uuid', 'language_matchers'],
            },
          },
        },
        rule_groups: {
          nestedResource: true,
          params: {
            limit: {
              defaultValue: '-1',
            },
            order_by: {
              defaultValue: 'name',
            },
          },
        },
      },
    },
  },
  {
    name: 'customer-gmail',
    path: '/:customerDomain/customer/gmail',
    component: CustomerEditGmail,
    private: true,
    permissions: 'customers.systemOptions',
  },
  {
    name: 'customer-chrome',
    path: '/:customerDomain/customer/chrome',
    component: CustomerEditChrome,
    private: true,
    permissions: 'customers.systemOptions',
  },
  {
    name: 'customer-zendesk',
    path: '/:customerDomain/customer/zendesk',
    component: CustomerEditZendesk,
    private: true,
    permissions: 'customers.systemOptions',
  },
  {
    name: 'customer-slack',
    path: '/:customerDomain/customer/slack',
    component: CustomerEditSlack,
    private: true,
    permissions: 'customers.systemOptions',
  },
  {
    name: 'customer-office',
    path: '/:customerDomain/customer/office',
    component: CustomerEditOffice,
    private: true,
    permissions: 'customers.systemOptions',
  },
  {
    name: 'customer-glia',
    path: '/:customerDomain/customer/glia',
    component: CustomerEditGlia,
    private: true,
    permissions: 'customers.systemOptions',
  },
  {
    name: 'customer-json-editor',
    path: '/:customerDomain/customer/json-editor',
    component: CustomerAdvanced,
    private: true,
    permissions: 'customers.advancedOptions',
  },
  {
    name: 'customer-index',
    path: '/:customerDomain/customer/index',
    component: CustomerIndex,
    private: true,
    permissions: 'customers.advancedOptions',
  },
  {
    name: 'customer-log-metrics',
    path: '/:customerDomain/customer/log-metrics',
    component: CustomerLogMetrics,
    private: true,
    permissions: 'customers.advancedOptions',
  },
  {
    name: 'customer-indexing-status',
    path: '/:customerDomain/customer/indexing-status',
    component: CustomerIndexingStatus,
    private: true,
    permissions: 'customers.advancedOptions',
  },
  {
    name: 'customer-index-health',
    path: '/:customerDomain/customer/index-health',
    component: CustomerIndexHealth,
    private: true,
    permissions: 'customers.advancedOptions',
    data: {
      actions: [fetchAllEnvelopes],
      params: {
        include_count: {
          defaultValue: '0',
        },
        envelopes: {
          nestedResource: true,
          params: envelopeListQueryParams,
        },
      },
    },
  },
  {
    name: 'customer-uploads',
    path: '/:customerDomain/customer/uploads',
    component: CustomerUploads,
    private: true,
    permissions: 'customers.list',
  },
  {
    name: 'customer-processing-status',
    path: '/:customerDomain/customer/processing-status',
    component: CustomerProcessingStatus,
    private: true,
    permissions: 'customers.advancedOptions',
  },
  {
    name: 'customer-contracts',
    path: '/:customerDomain/customer/customer-contracts',
    component: CustomerContractsManager,
    private: true,
    permissions: 'customers.advancedOptions',
    exact: true,
    data: {
      actions: [fetchCustomerContracts],
      params: {
        relationships: {
          defaultValue: [],
          list: true,
        },
        include_count: {
          defaultValue: '1',
        },
        customer_contracts: {
          nestedResource: true,
          params: {
            relationships: {
              defaultValue: [],
              list: true,
            },
            order_by: {
              defaultValue: 'created_at',
              options: ['created_at'],
            },
            order_desc: {
              defaultValue: 'true',
            },
            limit: {
              defaultValue: '25',
            },
            offset: {
              defaultValue: '0',
            },
          },
        },
      },
    },
  },
  {
    name: 'customer-import',
    path: '/:customerDomain/customer/import',
    component: CustomerImport,
    private: true,
    permissions: 'customers.advancedOptions',
    exact: true,
    data: {
      params: {
        only_integration_settings: { defaultValue: 'false' },
      },
    },
  },
  {
    name: 'customer',
    path: '/:customerDomain/customer',
    component: Customer,
    private: true,
    permissions: 'customers.generalOptions',
    data: {
      params: {
        scroll: {},
      },
    },
  },
  {
    name: 'create-customer',
    path: '/:customerDomain/create-customer',
    component: CreateCustomer,
    private: true,
    permissions: 'customers.advancedOptions',
  },
  {
    path: '/customer',
    component: (): null => null,
    private: true,
  },
];

export default customerRoutes;
